// Modules
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useVersionCheck from 'hooks/useVersionCheck';
import { useAppSelector } from 'app/store';
import { selectPartnerUser } from 'features/auth/authSlice';
import { toggleDarkMode } from 'features/ui/uiSlice';

// UI
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from 'components/catalyst/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarItem,
  SidebarSection,
} from 'components/catalyst/sidebar';
import { StackedLayout } from 'components/catalyst/stacked-layout';
import { Outlet } from 'react-router-dom';
import UserMenu from './UserMenu';
import {
  FolderIcon,
  DocumentTextIcon,
  CreditCardIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import { Heading } from 'components/catalyst/heading';
import { Text } from 'components/catalyst/text';

const navItems = [
  { label: 'TAB_PROJECTS', path: '/partners/projects', icon: FolderIcon },
  { label: 'TAB_QUOTES', path: '/partners/quotes', icon: DocumentTextIcon },
  { label: 'TAB_BILLING', path: '/partners/billing', icon: CreditCardIcon },
  { label: 'TAB_INTERNAL_CONTACTS', path: '/partners/internal-contacts', icon: BuildingOfficeIcon },
];

export const PartnerLayout = () => {
  const { t } = useTranslation('partner');
  const dispatch = useDispatch();
  const location = useLocation();
  const partnerUser = useAppSelector(selectPartnerUser);
  const { isDarkMode } = useAppSelector((state) => state.ui);

  useVersionCheck();

  const handleToggleTheme = () => {
    dispatch(toggleDarkMode());
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <StackedLayout
      navbar={
        <Navbar className="text-white">
          <div className="flex flex-col">
            {partnerUser?.companyName && (
              <>
                <Heading level={3} color="white">
                  {partnerUser?.companyName || t('PORTAL_TITLE')}
                </Heading>
              </>
            )}
            {partnerUser?.contactName && (
              <>
                <Text variant="paragraph-sm" color="white">
                  {t('PORTAL_WELCOME_BACK', { contactName: partnerUser.contactName })}
                </Text>
              </>
            )}
          </div>

          <NavbarSpacer />

          <NavbarSection className="max-lg:hidden">
            {navItems.map(({ label, path }) => (
              <NavbarItem
                key={path}
                href={path}
                current={isCurrentPath(path) ? 'true' : undefined}
                aria-label={t(label)}
                variant="light"
              >
                {t(label)}
              </NavbarItem>
            ))}
          </NavbarSection>
          <NavbarSpacer />
          <NavbarSection>
            <NavbarItem
              onClick={handleToggleTheme}
              aria-label="Toggle theme"
              tabIndex={0}
              variant="light"
            >
              {isDarkMode ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </NavbarItem>
            <UserMenu
              noButtons
              navigateOnLogout="/partners/login"
              variant="light"
              extraItems={[
                { label: t('TAB_TEAM'), icon: UserGroupIcon, path: '/partners/team' },
              ]}
            />
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader>
            <div className="flex flex-col p-2">
              <span className="text-lg font-semibold text-blue-600">{t('PORTAL_TITLE')}</span>
              {partnerUser?.companyName && (
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {partnerUser.companyName}
                </span>
              )}
              {partnerUser?.contactName && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {partnerUser.contactName}
                </span>
              )}
            </div>
          </SidebarHeader>
          <SidebarBody>
            <SidebarSection>
              {navItems.map(({ label, path, icon: Icon }) => (
                <SidebarItem
                  key={path}
                  href={path}
                  current={isCurrentPath(path)}
                  aria-label={t(label)}
                >
                  <Icon className="h-5 w-5" />
                  {t(label)}
                </SidebarItem>
              ))}
            </SidebarSection>
          </SidebarBody>
        </Sidebar>
      }
    >
      <Outlet />
    </StackedLayout>
  );
};
